import { useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import { Icon } from '@iconify/react';
import { vacancyStore } from 'stores/vacancyStore';
import axios from 'axios';
import { take } from 'lodash';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ACCEPT_FILE_TYPES = '.txt,.xlsx,.xls,.csv';

const isValidEmail = (value: string): boolean =>
  value.trim().length > 0 && EMAIL_REGEX.test(value.trim());

export interface AddParticipantsModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  vacancyId: string;
}

export const AddParticipantsModal = ({
  open,
  onClose,
  vacancyId,
  onSuccess
}: AddParticipantsModalProps) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [emailInput, setEmailInput] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  const [fileLoading, setFileLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmitEmail = useCallback(() => {
    const trimmed = emailInput.trim();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) return;
    if (emails.includes(trimmed)) return;
    setEmails((prev) => [...prev, trimmed]);
    setEmailInput('');
  }, [emailInput, emails]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmitEmail();
      }
    },
    [handleSubmitEmail]
  );

  const handleDeleteChip = useCallback((emailToRemove: string) => {
    setEmails((prev) => prev.filter((email) => email !== emailToRemove));
  }, []);

  const handleClose = useCallback(() => {
    setEmailInput('');
    setEmails([]);
    setTabIndex(0);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  }, [onClose]);

  const handleTabChange = useCallback((_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
    setError(null)
  }, []);

  const handleSend = useCallback(async () => {
    try {
      await vacancyStore.addCandidates(vacancyId, emails);
      onSuccess();
      handleClose();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setError(error.response?.data?.message);
      } else {
        setError('Неизвестная ошибка');
      }
    }
  }, [emails, handleClose, vacancyId, onSuccess]);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setFileLoading(true);
    try {
      const { emails, errorEmails } = await vacancyStore.extractEmails(file, vacancyId);
      const list = Array.isArray(emails) ? emails : [];
      const valid = list.filter((email) => typeof email === 'string' && isValidEmail(email));
      setEmails((prev) => Array.from(new Set([...prev, ...valid])));
      if (errorEmails.length) {
        const shortList = take(errorEmails, 6)
        const rest = errorEmails.length - 6
        const suffix = rest > 0 ? ` и еще ${rest}` : ''
        setError(`К вакансии уже прикреплены адреса: ${shortList.join(', ')}${suffix}`)
      }
    } catch {
      setError('Не удалось прочитать email из файла');
    } finally {
      setFileLoading(false);
      e.target.value = '';
    }
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="add-participants-dialog-title"
    >
      <Box sx={{ p: 2, pt: 1, pb: 4 }}>
        <DialogTitle sx={{ mb: 0 }} id="add-participants-dialog-title">
          Добавить участников
        </DialogTitle>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab
            label="Ввести вручную"
            id="add-participants-tab-0"
            aria-controls="add-participants-panel-0"
          />
          <Tab
            label="Извлечь из файла"
            id="add-participants-tab-1"
            aria-controls="add-participants-panel-1"
          />
        </Tabs>
        <DialogContent>
          {tabIndex === 0 && (
            <Stack
              spacing={2}
              id="add-participants-panel-0"
              role="tabpanel"
              aria-labelledby="add-participants-tab-0"
            >
              <Stack sx={{ pt: 1.5 }} direction="row" spacing={3} alignItems="center">
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  label="Email"
                  placeholder="email@example.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  inputProps={{ 'aria-label': 'Email участника' }}
                />
                <Button
                  variant="contained"
                  onClick={handleSubmitEmail}
                  disabled={
                    !emailInput.trim() ||
                    !isValidEmail(emailInput.trim()) ||
                    emails.includes(emailInput.trim())
                  }
                  aria-label="Добавить"
                  sx={{ minWidth: 100, flexShrink: 0 }}
                >
                  Добавить
                </Button>
              </Stack>
            </Stack>
          )}
          {tabIndex === 1 && (
            <Stack
              spacing={2}
              id="add-participants-panel-1"
              role="tabpanel"
              aria-labelledby="add-participants-tab-1"
            >
              <Box>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<Icon icon="mdi:file-upload-outline" width={20} height={20} />}
                  disabled={fileLoading}
                  aria-label="Выбрать файл"
                >
                  {fileLoading ? 'Загрузка...' : 'Выбрать файл (.txt, .xlsx, .xls, .csv)'}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPT_FILE_TYPES}
                    onChange={handleFileChange}
                    hidden
                    aria-label="Выберите файл .txt, .xlsx или .xls"
                  />
                </Button>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                  Поддерживаются текстовые файлы и таблицы Excel (.xlsx, .xls), .csv
                </Typography>
              </Box>
            </Stack>
          )}
          {emails.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 1 }}>
              {emails.map((email) => (
                <Chip
                  key={email}
                  label={email}
                  onDelete={() => handleDeleteChip(email)}
                  size="small"
                  aria-label={`Удалить ${email}`}
                />
              ))}
            </Box>
          )}
          {error && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
          <Button onClick={handleClose} aria-label="Закрыть">
            Закрыть
          </Button>
          <Button
            disabled={emails.length === 0}
            variant="contained"
            onClick={handleSend}
            aria-label="Отправить"
          >
            Отправить
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default AddParticipantsModal;
